import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, FileText, Users, CreditCard, BarChart3, HeadphonesIcon, RefreshCw,
  LogOut, Plus, TrendingUp, AlertTriangle, CheckCircle, Clock, DollarSign,
  MapPin, Phone, Mail, Calendar, ChevronRight, Activity, Target, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const ORG_TYPES = [
  { value: 'ESTADO', label: 'Estado' },
  { value: 'MUNICIPIO', label: 'Município' },
  { value: 'AUTARQUIA', label: 'Autarquia' },
  { value: 'SECRETARIA', label: 'Secretaria' },
  { value: 'CONTROLADORIA', label: 'Controladoria' },
  { value: 'PROCURADORIA', label: 'Procuradoria' },
  { value: 'CASA_CIVIL', label: 'Casa Civil' }
];

const CONTRACT_MODALITIES = [
  { value: 'DISPENSA', label: 'Dispensa de Licitação' },
  { value: 'INEXIGIBILIDADE', label: 'Inexigibilidade' },
  { value: 'LICITACAO', label: 'Licitação' },
  { value: 'PREGAO', label: 'Pregão Eletrônico' },
  { value: 'ADESAO_ATA', label: 'Adesão à Ata' }
];

const UF_LIST = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

export const AdminBlueGovPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [licenses, setLicenses] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [churnRisk, setChurnRisk] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientMetrics, setClientMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', type: '', cnpj: '', uf: '', responsible_name: '', responsible_email: '', responsible_phone: '' });
  const [newContract, setNewContract] = useState({ client_id: '', modality: '', start_date: '', end_date: '', monthly_value: '', seats_contracted: '' });
  const [newTicket, setNewTicket] = useState({ client_id: '', subject: '', description: '', priority: 'MEDIA' });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'clients') {
        const res = await api.get('/bluegov-admin/clients');
        setClients(res.data);
      } else if (activeTab === 'contracts') {
        const res = await api.get('/bluegov-admin/contracts');
        setContracts(res.data);
      } else if (activeTab === 'licenses') {
        const res = await api.get('/bluegov-admin/licenses/summary');
        setLicenses(res.data);
      } else if (activeTab === 'tickets') {
        const res = await api.get('/bluegov-admin/tickets');
        setTickets(res.data);
      } else if (activeTab === 'renewals') {
        const [renewalsRes, churnRes] = await Promise.all([
          api.get('/bluegov-admin/renewals'),
          api.get('/bluegov-admin/churn-risk')
        ]);
        setRenewals(renewalsRes.data);
        setChurnRisk(churnRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso restrito ao Admin BlueGov');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClientMetrics = async (clientId) => {
    try {
      const res = await api.get(`/bluegov-admin/metrics/${clientId}`);
      setClientMetrics(res.data);
      setSelectedClient(clientId);
    } catch (error) {
      toast.error('Erro ao carregar métricas');
    }
  };

  const handleCreateClient = async () => {
    try {
      const res = await api.post('/bluegov-admin/clients', newClient);
      toast.success(`Cliente criado! Tenant: ${res.data.tenant_id}`);
      setShowCreateClient(false);
      setNewClient({ name: '', type: '', cnpj: '', uf: '', responsible_name: '', responsible_email: '', responsible_phone: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cliente');
    }
  };

  const handleCreateContract = async () => {
    try {
      await api.post('/bluegov-admin/contracts', {
        ...newContract,
        monthly_value: parseFloat(newContract.monthly_value),
        seats_contracted: parseInt(newContract.seats_contracted)
      });
      toast.success('Contrato criado com sucesso');
      setShowCreateContract(false);
      setNewContract({ client_id: '', modality: '', start_date: '', end_date: '', monthly_value: '', seats_contracted: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar contrato');
    }
  };

  const handleCreateTicket = async () => {
    try {
      await api.post('/bluegov-admin/tickets', newTicket);
      toast.success('Ticket aberto com sucesso');
      setShowCreateTicket(false);
      setNewTicket({ client_id: '', subject: '', description: '', priority: 'MEDIA' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir ticket');
    }
  };

  const handleOffboard = async (clientId) => {
    if (!window.confirm('Tem certeza que deseja iniciar o processo de encerramento deste cliente?')) return;
    try {
      const res = await api.post(`/bluegov-admin/clients/${clientId}/offboard`);
      toast.success('Processo de encerramento iniciado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao iniciar encerramento');
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const getStatusColor = (status) => {
    const colors = {
      'ATIVO': 'bg-green-50 text-green-700',
      'INATIVO': 'bg-slate-100 text-slate-600',
      'EM_IMPLANTACAO': 'bg-blue-50 text-blue-700',
      'CHURN': 'bg-red-50 text-red-700',
      'PROSPECT': 'bg-blue-50 text-blue-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  // Calculate dashboard metrics
  const totalMRR = contracts.filter(c => c.status === 'VIGENTE').reduce((sum, c) => sum + (c.monthly_value || 0), 0);
  const activeClients = clients.filter(c => c.status === 'ATIVO').length;
  const totalSeats = licenses?.total_seats_contracted || 0;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="admin-bluegov-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">BlueGov Admin</h1>
            <p className="text-slate-500 mt-1">Gestão de clientes, contratos e operações SaaS</p>
          </div>
          <Badge className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm px-4 py-2">
            <Zap className="w-4 h-4 mr-2" />
            Painel do Fornecedor
          </Badge>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'MRR Total', value: `R$ ${totalMRR.toLocaleString('pt-BR')}`, icon: DollarSign, color: 'emerald' },
            { label: 'Clientes Ativos', value: activeClients, icon: Building2, color: 'blue' },
            { label: 'Assentos Totais', value: totalSeats, icon: Users, color: 'purple' },
            { label: 'Tickets Abertos', value: tickets.filter(t => t.status === 'ABERTO').length, icon: HeadphonesIcon, color: 'amber' }
          ].map((stat, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="clients" className="rounded-lg data-[state=active]:bg-white">
              <Building2 className="w-4 h-4 mr-2" />Clientes
            </TabsTrigger>
            <TabsTrigger value="contracts" className="rounded-lg data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />Contratos
            </TabsTrigger>
            <TabsTrigger value="licenses" className="rounded-lg data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />Licenças
            </TabsTrigger>
            <TabsTrigger value="metrics" className="rounded-lg data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />Métricas
            </TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-lg data-[state=active]:bg-white">
              <HeadphonesIcon className="w-4 h-4 mr-2" />Suporte
            </TabsTrigger>
            <TabsTrigger value="renewals" className="rounded-lg data-[state=active]:bg-white">
              <RefreshCw className="w-4 h-4 mr-2" />Renovação
            </TabsTrigger>
          </TabsList>

          {/* CLIENTS TAB */}
          <TabsContent value="clients" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Gestão de Clientes</h2>
              <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />Novo Cliente
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Cadastrar Novo Cliente</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Input
                      placeholder="Nome do órgão"
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Select onValueChange={(v) => setNewClient({ ...newClient, type: v })}>
                        <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                          {ORG_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select onValueChange={(v) => setNewClient({ ...newClient, uf: v })}>
                        <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>
                          {UF_LIST.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="CNPJ"
                      value={newClient.cnpj}
                      onChange={(e) => setNewClient({ ...newClient, cnpj: e.target.value })}
                    />
                    <Input
                      placeholder="Nome do responsável"
                      value={newClient.responsible_name}
                      onChange={(e) => setNewClient({ ...newClient, responsible_name: e.target.value })}
                    />
                    <Input
                      placeholder="E-mail do responsável"
                      type="email"
                      value={newClient.responsible_email}
                      onChange={(e) => setNewClient({ ...newClient, responsible_email: e.target.value })}
                    />
                    <Input
                      placeholder="Telefone (opcional)"
                      value={newClient.responsible_phone}
                      onChange={(e) => setNewClient({ ...newClient, responsible_phone: e.target.value })}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateClient(false)}>Cancelar</Button>
                    <Button onClick={handleCreateClient}>Cadastrar Cliente</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {clients.map((client) => (
                <Card key={client.client_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                          {client.uf}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{client.name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span>{ORG_TYPES.find(t => t.value === client.type)?.label}</span>
                            <span>•</span>
                            <span>CNPJ: {client.cnpj}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge className={getStatusColor(client.status)}>{client.status?.replace('_', ' ')}</Badge>
                            {client.metrics && (
                              <>
                                <span className="text-xs text-slate-400">{client.metrics.total_acts} atos</span>
                                <span className="text-xs text-slate-400">{client.metrics.active_users} usuários</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => fetchClientMetrics(client.client_id)}>
                          <BarChart3 className="w-4 h-4 mr-1" />Métricas
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleOffboard(client.client_id)}>
                          <LogOut className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* CONTRACTS TAB */}
          <TabsContent value="contracts" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Gestão de Contratos</h2>
              <Dialog open={showCreateContract} onOpenChange={setShowCreateContract}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />Novo Contrato
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Contrato</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Select onValueChange={(v) => setNewContract({ ...newContract, client_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(v) => setNewContract({ ...newContract, modality: v })}>
                      <SelectTrigger><SelectValue placeholder="Modalidade" /></SelectTrigger>
                      <SelectContent>
                        {CONTRACT_MODALITIES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Data Início</label>
                        <Input
                          type="date"
                          value={newContract.start_date}
                          onChange={(e) => setNewContract({ ...newContract, start_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Data Fim</label>
                        <Input
                          type="date"
                          value={newContract.end_date}
                          onChange={(e) => setNewContract({ ...newContract, end_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="number"
                        placeholder="Valor mensal (R$)"
                        value={newContract.monthly_value}
                        onChange={(e) => setNewContract({ ...newContract, monthly_value: e.target.value })}
                      />
                      <Input
                        type="number"
                        placeholder="Assentos contratados"
                        value={newContract.seats_contracted}
                        onChange={(e) => setNewContract({ ...newContract, seats_contracted: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateContract(false)}>Cancelar</Button>
                    <Button onClick={handleCreateContract}>Criar Contrato</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Cliente</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Modalidade</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Vigência</th>
                      <th className="text-right p-4 text-sm font-semibold text-slate-600">Valor Mensal</th>
                      <th className="text-center p-4 text-sm font-semibold text-slate-600">Assentos</th>
                      <th className="text-center p-4 text-sm font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => {
                      const client = clients.find(c => c.client_id === contract.client_id);
                      return (
                        <tr key={contract.contract_id} className="border-b hover:bg-slate-50">
                          <td className="p-4 font-medium">{client?.name || contract.client_id}</td>
                          <td className="p-4 text-sm">{CONTRACT_MODALITIES.find(m => m.value === contract.modality)?.label}</td>
                          <td className="p-4 text-sm text-slate-500">
                            {new Date(contract.start_date).toLocaleDateString('pt-BR')} - {new Date(contract.end_date).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="p-4 text-right font-medium">R$ {contract.monthly_value?.toLocaleString('pt-BR')}</td>
                          <td className="p-4 text-center">{contract.seats_used || 0}/{contract.seats_contracted}</td>
                          <td className="p-4 text-center">
                            <Badge className={contract.status === 'VIGENTE' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}>
                              {contract.status}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LICENSES TAB */}
          <TabsContent value="licenses" className="space-y-4">
            {licenses && (
              <>
                <div className="grid md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-4xl font-bold text-blue-600">{licenses.total_seats_contracted}</p>
                      <p className="text-sm text-slate-500 mt-1">Assentos Contratados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-4xl font-bold text-green-600">{licenses.total_seats_used}</p>
                      <p className="text-sm text-slate-500 mt-1">Assentos Utilizados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-4xl font-bold text-blue-600">{licenses.utilization_rate}%</p>
                      <p className="text-sm text-slate-500 mt-1">Taxa de Utilização</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-4xl font-bold text-amber-600">{licenses.expansion_capacity}</p>
                      <p className="text-sm text-slate-500 mt-1">Capacidade Livre</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Detalhamento por Cliente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold">Cliente</th>
                          <th className="text-center p-3 text-sm font-semibold">Contratados</th>
                          <th className="text-center p-3 text-sm font-semibold">Utilizados</th>
                          <th className="text-center p-3 text-sm font-semibold">Utilização</th>
                          <th className="text-center p-3 text-sm font-semibold">Upsell</th>
                        </tr>
                      </thead>
                      <tbody>
                        {licenses.details?.map((detail, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-3 font-medium">{detail.client_name}</td>
                            <td className="p-3 text-center">{detail.seats_contracted}</td>
                            <td className="p-3 text-center">{detail.seats_used}</td>
                            <td className="p-3 text-center">
                              <Badge className={detail.utilization_rate >= 80 ? 'bg-green-50 text-green-700' : detail.utilization_rate >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}>
                                {detail.utilization_rate}%
                              </Badge>
                            </td>
                            <td className="p-3 text-center">
                              {detail.upsell_potential && (
                                <Badge className="bg-blue-50 text-blue-700">
                                  <Target className="w-3 h-3 mr-1" />Potencial
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* METRICS TAB */}
          <TabsContent value="metrics" className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Select onValueChange={(v) => fetchClientMetrics(v)}>
                <SelectTrigger className="w-64"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {clientMetrics && (
              <>
                <div className="grid md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-3xl font-bold">{clientMetrics.metrics.total_acts}</p>
                          <p className="text-sm text-slate-500">Total de Atos</p>
                        </div>
                        <Badge className={clientMetrics.churn_risk === 'BAIXO' ? 'bg-green-50 text-green-700' : clientMetrics.churn_risk === 'MÉDIO' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}>
                          Risco: {clientMetrics.churn_risk}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-3xl font-bold">{clientMetrics.metrics.completion_rate}%</p>
                      <p className="text-sm text-slate-500">Taxa de Conclusão</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-3xl font-bold">{clientMetrics.expansion_indicators.engagement_score}</p>
                      <p className="text-sm text-slate-500">Score de Engajamento</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Curva de Adoção</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={clientMetrics.adoption_curve}>
                          <defs>
                            <linearGradient id="colorActs" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Area type="monotone" dataKey="acts" stroke="#3B82F6" fill="url(#colorActs)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* TICKETS TAB */}
          <TabsContent value="tickets" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Suporte / Tickets</h2>
              <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />Abrir Ticket
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Abrir Ticket de Suporte</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Select onValueChange={(v) => setNewTicket({ ...newTicket, client_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Cliente" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Assunto"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    />
                    <Textarea
                      placeholder="Descrição do problema"
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    />
                    <Select onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                      <SelectTrigger><SelectValue placeholder="Prioridade" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BAIXA">Baixa</SelectItem>
                        <SelectItem value="MEDIA">Média</SelectItem>
                        <SelectItem value="ALTA">Alta</SelectItem>
                        <SelectItem value="CRITICA">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Cancelar</Button>
                    <Button onClick={handleCreateTicket}>Abrir Ticket</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {tickets.map((ticket) => (
                <Card key={ticket.ticket_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{ticket.subject}</span>
                          <Badge className={
                            ticket.priority === 'CRITICA' ? 'bg-red-50 text-red-700' :
                            ticket.priority === 'ALTA' ? 'bg-amber-50 text-amber-700' :
                            ticket.priority === 'MEDIA' ? 'bg-blue-50 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }>{ticket.priority}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{ticket.description}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(ticket.created_at).toLocaleString('pt-BR')} • SLA: {ticket.sla_hours}h
                        </p>
                      </div>
                      <Badge className={
                        ticket.status === 'ABERTO' ? 'bg-blue-50 text-blue-700' :
                        ticket.status === 'RESOLVIDO' ? 'bg-green-50 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }>{ticket.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* RENEWALS TAB */}
          <TabsContent value="renewals" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Renewals */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Renovações Próximas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {renewals.slice(0, 5).map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{r.client_name}</p>
                          <p className="text-sm text-slate-500">{r.uf} • Vence: {new Date(r.end_date).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            r.urgency === 'CRITICA' ? 'bg-red-50 text-red-700' :
                            r.urgency === 'ALTA' ? 'bg-amber-50 text-amber-700' :
                            'bg-blue-50 text-blue-700'
                          }>{r.days_remaining} dias</Badge>
                          <p className="text-xs text-slate-500 mt-1">R$ {r.annual_value?.toLocaleString('pt-BR')}/ano</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Churn Risk */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Risco de Churn
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {churnRisk.slice(0, 5).map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{r.client_name}</p>
                          <p className="text-sm text-slate-500">{r.uf}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            r.risk_level === 'ALTO' ? 'bg-red-50 text-red-700' :
                            r.risk_level === 'MÉDIO' ? 'bg-amber-50 text-amber-700' :
                            'bg-green-50 text-green-700'
                          }>Risco: {r.risk_level}</Badge>
                          <p className="text-xs text-slate-500 mt-1">Score: {r.risk_score}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Client Metrics Modal would go here if needed */}
      </div>
    </AppLayout>
  );
};

export default AdminBlueGovPage;
